import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Modal } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTicketAlt, 
  FaFilm,
  FaTimes,
  FaExclamationTriangle,
  FaEye,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import ModernLoader from '../components/common/ModernLoader';
import MovieTicket from '../components/ticket/MovieTicket';
import { useAuth } from '@clerk/clerk-react';
import moment from 'moment';

const MyBookings = () => {
  const { getToken, isSignedIn } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModal, setCancelModal] = useState({ show: false, booking: null });
  const [cancelling, setCancelling] = useState(false);
  const [ticketModal, setTicketModal] = useState({ show: false, booking: null });
  const [currentPage, setCurrentPage] = useState(1);
  const bookingsPerPage = 9;

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      if (!isSignedIn) {
        setLoading(false);
        return;
      }
      
      const token = await getToken();
      const timestamp = new Date().getTime();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/bookings/my-bookings?_t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const canCancelBooking = (booking) => {
    if (booking.status === 'cancelled' || !booking?.show?.showDate || !booking?.show?.showTime) return false;
    
    const showDateTime = moment(`${moment(booking.show.showDate).format('YYYY-MM-DD')} ${booking.show.showTime}`, 'YYYY-MM-DD h:mm A');
    const minutesUntilShow = showDateTime.diff(moment(), 'minutes');
    
    return minutesUntilShow > 15;
  };

  const handleCancelBooking = async () => {
    if (!cancelModal.booking) return;
    
    setCancelling(true);
    try {
      const token = await getToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/bookings/${cancelModal.booking._id}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel booking');
      }
      
      setBookings(prev => prev.map(booking => 
        booking._id === cancelModal.booking._id 
          ? { ...booking, status: 'cancelled', cancelledAt: new Date() }
          : booking
      ));
      
      toast.success(`Booking cancelled! Refund: ₹${Math.round(cancelModal.booking.totalAmount * 0.9)}`);
      setCancelModal({ show: false, booking: null });
    } catch (error) {
      toast.error(error.message || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <ModernLoader text="Loading Bookings" />;
  
  if (!isSignedIn) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#1f2025' }}>
        <div className="text-center">
          <h4 className="text-white mb-3">Please Sign In</h4>
          <p className="text-light">Sign in to view your bookings</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#1f2025', paddingTop: '100px' }}>
      <style>
        {`
          @keyframes borderGlow {
            0%, 100% { box-shadow: 0 0 20px rgba(255, 107, 107, 0.4); }
            50% { box-shadow: 0 0 30px rgba(255, 107, 107, 0.6); }
          }
          .booking-card {
            border: 2px solid #ff6b6b !important;
            animation: borderGlow 3s ease-in-out infinite;
            transition: all 0.3s ease;
          }
          .booking-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(255, 107, 107, 0.3) !important;
          }
          .pagination-input {
            background-color: #2a2d35 !important;
            border: 2px solid #ff6b6b !important;
            color: #ffffff !important;
            border-radius: 8px !important;
            padding: 5px 8px;
            text-align: center;
            font-weight: 500;
            width: 50px;
            font-size: 0.85rem;
            transition: all 0.3s ease;
          }
          .pagination-input:focus {
            outline: none;
            box-shadow: 0 0 15px rgba(255, 107, 107, 0.5);
          }
          .pagination-btn {
            background-color: #2a2d35 !important;
            border: 2px solid #ff6b6b !important;
            color: #ffffff !important;
            border-radius: 8px !important;
            padding: 6px 10px;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }
          .pagination-btn:hover:not(:disabled) {
            background-color: #ff6b6b !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);
          }
          .pagination-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .pagination-info {
            color: #ffffff;
            font-size: 0.8rem;
            font-weight: 500;
            white-space: nowrap;
          }
          @media (max-width: 576px) {
            .booking-card {
              border: 1.5px solid #ff6b6b !important;
            }
            .booking-card .card-body {
              padding: 1rem !important;
            }
            .pagination-input {
              width: 45px;
              padding: 4px 6px;
              font-size: 0.75rem;
            }
            .pagination-btn {
              padding: 5px 8px;
            }
            .pagination-info {
              font-size: 0.7rem;
            }
          }
        `}
      </style>
      
      <Container>
        <div className="text-center mb-5" data-aos="fade-down" data-aos-duration="800">
          <FaTicketAlt size={48} className="text-danger mb-3" data-aos="bounce" data-aos-delay="200" />
          <h2 className="text-white fw-bold mb-2" data-aos="zoom-in" data-aos-delay="400">My Bookings</h2>
          <p className="text-light" data-aos="fade-up" data-aos-delay="600">Manage your movie tickets</p>
        </div>

        <AnimatePresence>
          {bookings.length > 0 ? (
            <>
              <Row className="g-3 justify-content-center">
                {bookings
                  .slice((currentPage - 1) * bookingsPerPage, currentPage * bookingsPerPage)
                  .map((booking, index) => (
                <Col key={booking._id} xl={4} lg={6} md={6} xs={12}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="h-100"
                    data-aos="flip-left"
                    data-aos-delay={index * 100}
                    data-aos-duration="600"
                  >
                    <Card className="booking-card bg-secondary border-0 text-white h-100">
                      <Card.Body className="p-2 p-sm-3 d-flex flex-column">
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                          <div className="d-flex align-items-center flex-grow-1 min-w-0">
                            <FaFilm className="text-danger me-2 flex-shrink-0" size={16} />
                            <div className="flex-grow-1 min-w-0">
                              <h6 className="text-white mb-1 fw-bold" style={{ fontSize: 'clamp(0.75rem, 2vw, 0.9rem)', wordBreak: 'break-word' }}>
                                {booking.show?.movie?.title || booking.movieTitle || 'Movie Title'}
                                {((booking.show?.movie && !booking.show.movie.isActive) || (booking.movieIsActive === false)) && (
                                  <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.6rem' }}>No Longer Available</Badge>
                                )}
                              </h6>
                              <small className="text-light" style={{ fontSize: '0.7rem' }}>#{booking.bookingId}</small>
                            </div>
                          </div>
                          <Badge 
                            bg={booking.status === 'confirmed' ? 'success' : 'danger'} 
                            className="px-2 py-1 flex-shrink-0"
                            style={{ fontSize: '0.65rem' }}
                          >
                            {booking.status === 'confirmed' ? 'Confirmed' : 'Cancelled'}
                          </Badge>
                        </div>

                        {/* Details */}
                        <div className="mb-2 flex-grow-1">
                          <div className="d-flex align-items-center mb-2 gap-2">
                            <FaMapMarkerAlt className="text-danger flex-shrink-0" size={12} />
                            <div className="min-w-0">
                              <div className="text-white fw-semibold" style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)', wordBreak: 'break-word' }}>
                                {booking.show?.theater?.name || booking.theaterName || 'Theater'}
                              </div>
                              <small className="text-light" style={{ fontSize: '0.65rem' }}>
                                {booking.show?.theater?.city || booking.show?.theater?.location || 'City'}
                              </small>
                            </div>
                          </div>
                          
                          <div className="d-flex align-items-center mb-2 gap-2">
                            <FaCalendarAlt className="text-danger flex-shrink-0" size={12} />
                            <div className="min-w-0">
                              <div className="text-white fw-semibold" style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)' }}>
                                {moment(booking.show?.showDate || booking.showDate).format('MMM DD, YYYY')}
                              </div>
                              <small className="text-light" style={{ fontSize: '0.65rem' }}>
                                {booking.show?.showTime || booking.showTime || 'Time TBD'}
                              </small>
                            </div>
                          </div>

                          {/* Seats */}
                          <div className="mb-2">
                            <div className="text-white fw-semibold mb-1" style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)' }}>
                              Seats ({booking.seats?.length || 0})
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {booking.seats?.slice(0, 4).map(seat => (
                                <Badge key={seat} bg="dark" className="px-2 py-1" style={{ fontSize: '0.65rem' }}>
                                  {seat}
                                </Badge>
                              ))}
                              {booking.seats?.length > 4 && (
                                <Badge bg="dark" className="px-2 py-1" style={{ fontSize: '0.65rem' }}>
                                  +{booking.seats.length - 4}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="d-flex justify-content-between align-items-center pt-2 border-top border-secondary mt-auto gap-2 flex-wrap">
                          <div>
                            <div className="text-white mb-0 fw-bold" style={{ fontSize: 'clamp(0.9rem, 2vw, 1.25rem)' }}>₹{booking.totalAmount}</div>
                            <small className={`badge ${booking.paymentStatus === 'completed' ? 'bg-success' : 'bg-warning'}`} style={{ fontSize: '0.6rem' }}>
                              {booking.paymentStatus === 'completed' ? 'Paid' : 'Pending'}
                            </small>
                          </div>
                          
                          {booking.status === 'confirmed' && (
                            <div className="d-flex gap-1 flex-wrap justify-content-end">
                              <Button
                                variant="outline-light"
                                size="sm"
                                className="px-2 py-1"
                                style={{ fontSize: '0.65rem' }}
                                onClick={() => setTicketModal({ show: true, booking })}
                                disabled={(booking.show?.movie && !booking.show.movie.isActive) || (booking.movieIsActive === false)}
                              >
                                <FaEye size={10} className="me-1" />
                                View
                              </Button>
                              {canCancelBooking(booking) && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="px-2 py-1"
                                  style={{ fontSize: '0.65rem' }}
                                  onClick={() => setCancelModal({ show: true, booking })}
                                >
                                  <FaTimes size={10} className="me-1" />
                                  Cancel
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {booking.status === 'cancelled' && (
                          <div className="mt-2 pt-2 border-top border-secondary">
                            <small className="text-danger" style={{ fontSize: '0.7rem' }}>
                              Cancelled on {moment(booking.cancelledAt).format('MMM DD')}
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
                ))}
              </Row>
              
              {bookings.length > bookingsPerPage && (
                <div className="d-flex justify-content-center align-items-center mt-4 mt-md-5 px-2 gap-3 flex-wrap">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    title="First Page"
                  >
                    <FaChevronLeft size={12} />
                    <FaChevronLeft size={12} style={{ marginLeft: '-6px' }} />
                  </button>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    title="Previous Page"
                  >
                    <FaChevronLeft size={14} />
                  </button>
                  
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="number"
                      className="pagination-input"
                      value={currentPage}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        const totalPages = Math.ceil(bookings.length / bookingsPerPage);
                        setCurrentPage(Math.max(1, Math.min(val, totalPages)));
                      }}
                      min="1"
                      max={Math.ceil(bookings.length / bookingsPerPage)}
                    />
                    <span className="pagination-info">/ {Math.ceil(bookings.length / bookingsPerPage)}</span>
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(bookings.length / bookingsPerPage)))}
                    disabled={currentPage === Math.ceil(bookings.length / bookingsPerPage)}
                    title="Next Page"
                  >
                    <FaChevronRight size={14} />
                  </button>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(Math.ceil(bookings.length / bookingsPerPage))}
                    disabled={currentPage === Math.ceil(bookings.length / bookingsPerPage)}
                    title="Last Page"
                  >
                    <FaChevronRight size={12} />
                    <FaChevronRight size={12} style={{ marginLeft: '-6px' }} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5" data-aos="fade-up" data-aos-duration="800">
              <FaTicketAlt size={80} className="text-muted mb-4" data-aos="bounce" data-aos-delay="200" />
              <h3 className="text-white mb-3" data-aos="zoom-in" data-aos-delay="400">No Bookings Found</h3>
              <p className="text-light mb-4 lead" data-aos="fade-up" data-aos-delay="600">Start booking your favorite movies!</p>
              <Button variant="danger" size="lg" href="/movies" className="px-5 py-3" data-aos="slide-up" data-aos-delay="800">
                <FaFilm className="me-2" />
                Browse Movies
              </Button>
            </div>
          )}
        </AnimatePresence>

        {/* Cancel Modal */}
        <Modal 
          show={cancelModal.show} 
          onHide={() => setCancelModal({ show: false, booking: null })}
          centered
        >
          <div style={{ backgroundColor: '#1f2025' }}>
            <Modal.Header className="border-secondary" style={{ backgroundColor: '#1f2025' }}>
              <div className="d-flex align-items-center">
                <FaExclamationTriangle className="text-warning me-3" size={24} />
                <div>
                  <Modal.Title className="text-white">Cancel Booking</Modal.Title>
                  <small className="text-light">This action cannot be undone</small>
                </div>
              </div>
            </Modal.Header>
            
            <Modal.Body style={{ backgroundColor: '#1f2025' }}>
              {cancelModal.booking && (
                <>
                  <div className="mb-4">
                    <h6 className="text-white">{cancelModal.booking.show?.movie?.title || cancelModal.booking.movieTitle}</h6>
                    <small className="text-light">Booking ID: #{cancelModal.booking.bookingId}</small>
                  </div>
                  
                  <Card className="bg-dark border-0 mb-4">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-light">Original Amount</span>
                        <span className="text-white fw-semibold">₹{cancelModal.booking.totalAmount}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-light">Cancellation Fee (10%)</span>
                        <span className="text-danger">-₹{Math.round(cancelModal.booking.totalAmount * 0.1)}</span>
                      </div>
                      <hr className="border-secondary" />
                      <div className="d-flex justify-content-between">
                        <span className="text-white fw-bold">Refund Amount</span>
                        <span className="text-success fw-bold h5 mb-0">₹{Math.round(cancelModal.booking.totalAmount * 0.9)}</span>
                      </div>
                    </Card.Body>
                  </Card>
                  
                  <small className="text-light">
                    Refund will be processed within 5-7 business days
                  </small>
                </>
              )}
            </Modal.Body>
            
            <Modal.Footer className="border-secondary" style={{ backgroundColor: '#1f2025' }}>
              <Button 
                variant="outline-light"
                onClick={() => setCancelModal({ show: false, booking: null })}
                disabled={cancelling}
              >
                Keep Booking
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelBooking}
                disabled={cancelling}
              >
                {cancelling ? 'Processing...' : 'Confirm Cancellation'}
              </Button>
            </Modal.Footer>
          </div>
        </Modal>

        {/* Ticket Modal */}
        <Modal 
          show={ticketModal.show} 
          onHide={() => setTicketModal({ show: false, booking: null })}
          centered
          size="lg"
        >
          <Modal.Header closeButton className="bg-secondary border-0">
            <Modal.Title className="text-white">Movie Ticket</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark p-0">
            {ticketModal.booking && <MovieTicket booking={ticketModal.booking} />}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default MyBookings;
